import React, { useState } from "react";
import { motion } from "framer-motion";
import { COUNTING_ENGINE_THEME, countingSequenceChoices, countingSequenceValues, countingSpokenWord } from "../../../../data/countingLadderCatalog";
import { useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import { CardSlot, CountingPanel, CountingRoot, NumberCard, PanelChip, POP_SPRING, hexAlpha, useCompleteOnce, useCountingDrag, useWrongFlash } from "../CountingPiece";
import PlaceValueBuilder from "./PlaceValueBuilder";

const SLOT = "sequence-slot";
const SLOT_DATA = { kind: "slot" };

/**
 * Repair the number strip: [before] [ ? ] [after]. Drag a number card into
 * the gap (or tap it) — the right one snaps in, the wrong one shakes home.
 */
const SequenceRepairGame = (props) => {
  const { activity, speak, sfx, onComplete, onWrong, reduceMotion = false } = props;
  const accent = COUNTING_ENGINE_THEME.sequenceRepair.accent;
  const { target, beforeValue, afterValue } = countingSequenceValues(activity);
  const [solved, setSolved] = useState(false);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { flash, isWrong } = useWrongFlash();

  const choose = (value) => {
    if (solved) return false;
    if (value !== target) {
      flash(value);
      onWrong?.();
      return false;
    }
    setSolved(true);
    sfx?.("snap");
    speak?.(countingSpokenWord(value), { interrupt: true });
    setTimeout(() => sfx?.("correct"), 120);
    setBurst((b) => b + 1);
    complete();
    return true;
  };

  const { dragProps, registerTarget, hoverId, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ payload, targetId }) => (targetId === SLOT ? choose(payload) : false),
  });
  const slotRef = useDropTarget(registerTarget, SLOT, SLOT_DATA);

  if (activity.number === 79) return <PlaceValueBuilder {...props} />;

  const choices = countingSequenceChoices(activity);
  const ghost = (value) => (
    <NumberCard as="div" accent={accent} state="selected" wrapStyle={{ width: "100%", height: "100%" }} style={{ width: "100%", height: "100%", minWidth: 0, minHeight: 0 }}>
      {value}
    </NumberCard>
  );

  return (
    <CountingRoot>
      <CountingPanel grow tint={accent} chip={<PanelChip text={solved ? "Strip repaired" : "Fix the strip"} accent={accent} />}>
        <div style={{ position: "relative", display: "flex", flexWrap: "wrap", gap: "clamp(8px, 2vmin, 16px)", alignItems: "center", justifyContent: "center", padding: "calc(var(--tile) * 0.22) calc(var(--tile) * 0.3)", maxWidth: "100%", boxSizing: "border-box" }}>
          {/* the number-strip rail the cards sit on */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute", left: 0, right: 0, top: "50%", height: "calc(var(--tile) * 0.36)", marginTop: "calc(var(--tile) * -0.18)", zIndex: -1,
              borderRadius: 999, background: `linear-gradient(180deg, ${hexAlpha(accent, 0.22)}, ${hexAlpha(accent, 0.34)})`, boxShadow: `inset 0 2px 4px ${hexAlpha(accent, 0.3)}`,
            }}
          />
          <NumberCard as="div" accent={accent} state="placed" size="lg">{beforeValue}</NumberCard>
          <CardSlot slotRef={slotRef} accent={accent} hover={hoverId === SLOT} filled={solved} label={solved ? `${target} in place` : "Missing number slot"} size="lg">
            {solved ? (
              <motion.div initial={reduceMotion ? false : { scale: 0.6 }} animate={{ scale: 1 }} transition={POP_SPRING} style={{ display: "inline-flex" }}>
                <NumberCard as="div" accent={accent} state="correct" size="lg">{target}</NumberCard>
              </motion.div>
            ) : (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M9 9.5a3 3 0 115 2.2c-1.2.8-2 1.5-2 3" /><circle cx="12" cy="18.5" r="1.1" fill="currentColor" stroke="none" /></svg>
            )}
          </CardSlot>
          <NumberCard as="div" accent={accent} state="placed" size="lg">{afterValue}</NumberCard>
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile) * 0.16)", justifyContent: "center", minHeight: "calc(var(--tile) * 1.4)", alignItems: "center" }}>
        {choices.map((value) => {
          const seated = solved && value === target;
          const dp = dragProps(value, ghost);
          return (
            <motion.div
              key={value}
              initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
              animate={{ scale: seated ? 0.001 : 1, opacity: seated ? 0 : 1 }}
              transition={reduceMotion ? { duration: 0 } : POP_SPRING}
              style={{ display: "inline-flex" }}
            >
              <NumberCard
                {...dp}
                accent={accent}
                reduceMotion={reduceMotion}
                disabled={solved}
                state={isWrong(value) ? "wrong" : "idle"}
                onClick={guardTap(() => choose(value))}
                style={{ ...dp.style, cursor: solved ? "default" : "grab" }}
              >
                {value}
              </NumberCard>
            </motion.div>
          );
        })}
      </div>
      <GhostLayer />
    </CountingRoot>
  );
};

export default SequenceRepairGame;
